import styled from "@emotion/styled";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import type { AutocompleteOption } from "@skilitics/designer-ui-components";
import {
  Alert,
  FormAutocomplete,
  useCommitChange,
  useField,
  useFieldContext,
  useFieldLabelAndDescription,
  useFieldPlaceholder,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { isReferencedValue, referencedNodeValue } from "@skilitics/nautilus-core-designer-data";

import { useWouldCreateValueReferenceCycle } from "../references/useWouldCreateValueReferenceCycle.ts";
import type { ValueBindChoice, ValueBindChoices } from "./useValueBindChoices.ts";
import { useValueConfigSummary } from "./useValueConfigSummary.ts";

const RefusalAlert = styled(Alert)`
  grid-column: 1 / -1;
`;

export interface BindingPickerProps {
  options: AutocompleteOption[];
  /** the picked option's key, "" for none */
  value: string;
  onChange: (valueKey: string) => void;
  label?: ReactNode;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  dataTest?: string;
}

/** Picks one value to bind to, grouped by category. Trades in option keys, not configs. */
export const BindingPicker = ({ options, value, onChange, dataTest, ...props }: BindingPickerProps) => {
  const selectedOption = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);

  return (
    <FormAutocomplete
      {...props}
      options={options}
      value={selectedOption}
      onChange={(picked) => onChange(Array.isArray(picked) ? (picked[0] ?? "") : picked || "")}
      freeSolo={false}
      groupByOptionGroup
      data-test={dataTest}
    />
  );
};

export type FieldBindingPickerProps = Omit<BindingPickerProps, "options" | "value" | "onChange" | "helperText"> & {
  choices: ValueBindChoices;
  /** the node holding this binding — a pick looping back to it is refused. Omit to skip the check. */
  forNodeId?: number;
  /** an accepted pick, for callers that write sibling fields off the same choice */
  onPick?: (choice: ValueBindChoice) => void;
};

/**
 * The binding picker wired to a form field holding the `ReferencedValue` it edits. Trades the
 * config to and from the option key the picker shows. Turns down a pick that would loop back to
 * `forNodeId` rather than marking the form invalid: the field keeps the binding it had, and a loop
 * left on screen reads as if it took.
 */
export const FieldBindingPicker = ({ choices, forNodeId, onPick, label, ...props }: FieldBindingPickerProps) => {
  const { options, choiceByOptionValue, selectionFor } = choices;
  const formatMessage = useFormatMessage(formatStrings);
  const summariseValueConfig = useValueConfigSummary();
  const wouldCreateValueReferenceCycle = useWouldCreateValueReferenceCycle();
  const commitChange = useCommitChange();

  const { dataTest, name } = useFieldContext();
  const [ambientLabel, helperText] = useFieldLabelAndDescription(name);
  const fieldPlaceholder = useFieldPlaceholder(name);
  // the field may hold a literal (ValueConfigEditor's unbound branch shares this name), so only a
  // reference selects an option
  const [{ value: fieldValue }, , { setValue }] = useField<unknown>(name);
  const boundValue = isReferencedValue(fieldValue) ? fieldValue : undefined;

  // Named so the refusal can say which value was turned down.
  const [refusedValueTitle, setRefusedValueTitle] = useState<string | undefined>(undefined);

  const selectedKey = selectionFor(boundValue);

  const pickerOptions = useMemo(() => {
    // a binding whose value is no longer on offer (target deleted, or out of scope) keeps its
    // own option, so it reads as the stored binding rather than as nothing picked
    if (!boundValue || choiceByOptionValue.has(selectedKey)) return options;

    return [
      { label: String(summariseValueConfig(boundValue) ?? formatMessage("unavailableValue")), value: selectedKey },
      ...options,
    ];
  }, [boundValue, selectedKey, choiceByOptionValue, formatMessage, options, summariseValueConfig]);

  const handleChange = (valueKey: string) => {
    const choice = choiceByOptionValue.get(valueKey);
    if (!choice) return;
    const targetNodeId = referencedNodeValue(choice.config)?.nodeId;
    if (
      targetNodeId !== undefined &&
      forNodeId !== undefined &&
      wouldCreateValueReferenceCycle(forNodeId, targetNodeId)
    ) {
      // left unwritten, so the field keeps the binding it had
      setRefusedValueTitle(choice.option.label);
      return;
    }
    setRefusedValueTitle(undefined);
    setValue(choice.config)
      .then(() => {
        onPick?.(choice);
        commitChange();
      })
      .catch(console.warn);
  };

  return (
    <>
      <BindingPicker
        {...props}
        options={pickerOptions}
        value={selectedKey}
        onChange={handleChange}
        label={label ?? ambientLabel}
        helperText={helperText}
        placeholder={props.placeholder ?? fieldPlaceholder}
        dataTest={dataTest}
      />
      {refusedValueTitle !== undefined && (
        <RefusalAlert severity="warning" data-test={dataTest ? `${dataTest}-cycle-warning` : "binding-cycle-warning"}>
          {formatMessage("cycleRefused", { value: refusedValueTitle })}
        </RefusalAlert>
      )}
    </>
  );
};

const formatStrings = {
  unavailableValue: {
    en: "Unavailable value",
  },
  cycleRefused: {
    en: "“{value}” already depends on this one, so binding to it would lead back to itself and couldn't be worked out. Pick another value.",
  },
} satisfies FormatMessages;
